import { Icon } from '@dynamic/ui';
import { FC } from 'react';

import { MenuItem } from '#/client';
import { useConfig } from '#/hooks';
import { MenuFocusStyle, NavigationType } from '#/service/constants';
import { InternalPageId, MenuItemDisplayType } from '#/service/constants/menu';
import { Dictionary, techSearchParams } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { MenuButton, MenuItemBoxBg, MenuLink, MenuNavLink } from './components';
import { SettingsMenuLink } from './SettingsLink';
import { getMenuItemInformation, getNavigationTarget } from './utils/menu';

// menu item target -> title dictionary key
const itemTargetLabels: Dictionary<string, string> = {
  search: 'button.search',
  login: 'button.login',
};

export interface ItemLinkProps {
  item: MenuItem;
  defaultSelectedItem: string;
  showIcons?: boolean;
  onClick?: () => void;
  inHamburger?: boolean;
}

export const ItemLink: FC<ItemLinkProps> = ({
  item,
  defaultSelectedItem,
  showIcons,
  onClick,
  inHamburger,
}) => {
  const {
    destinationId,
    destinationType,
    displayType,
    title,
    icon,
    buttonVariant,
  } = getMenuItemInformation(item);

  const {
    theme: {
      menu: { focus: focusStyle },
    },
  } = useConfig();

  const hasIcon =
    !!icon &&
    (MenuItemDisplayType.Icon === displayType ||
      MenuItemDisplayType.IconText === displayType ||
      showIcons);

  const isMore = destinationId === InternalPageId.More;

  const hasText =
    MenuItemDisplayType.Text === displayType ||
    MenuItemDisplayType.IconText === displayType ||
    MenuItemDisplayType.Button === displayType;

  const children = (
    <>
      {(hasIcon || hasText) && (
        <MenuItemBoxBg $iconOnly={hasIcon && !hasText} className="box">
          {hasIcon && <Icon key="icon" name={icon} />}
          {hasText && (
            <span className="title" key="title">
              {tb(title)}
            </span>
          )}
        </MenuItemBoxBg>
      )}
    </>
  );

  const ariaLabel = item.display.title ?? tb(itemTargetLabels[destinationId]);

  const search = techSearchParams(document.location.search);

  const commonProps = {
    key: destinationId,
    'aria-label': ariaLabel,
    $buttonVariant: buttonVariant,
    $focusStyle: buttonVariant ? MenuFocusStyle.None : focusStyle,
    onClick,
    $inHamburger: inHamburger,
    children,
  };

  if (isMore) {
    const { children, ...menuButtonProps } = commonProps;

    return (
      <SettingsMenuLink menuButtonProps={menuButtonProps}>
        {children}
      </SettingsMenuLink>
    );
  }

  // Login page will not be a route but will prompt a popup
  if (destinationId === InternalPageId.Login) {
    return <MenuButton role="menuitem" type="button" {...commonProps} />;
  }

  const { internalUrl, externalUrl } = getNavigationTarget(
    item,
    defaultSelectedItem
  );

  if (destinationType === NavigationType.External) {
    return (
      <MenuLink
        {...commonProps}
        href={externalUrl}
        role="menuitem"
        target="_blank"
      />
    );
  }

  return (
    <MenuNavLink
      {...commonProps}
      role="menuitem"
      to={{ pathname: internalUrl, search: search }}
    />
  );
};
